import { FC } from 'react';
import { Avatar, Card, Descriptions, Flex, Tag, Typography } from 'antd';
import { TbCalendarEvent, TbCalendarSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import SmallTag from '@/shared/components/tags/SmallTag';
import { dayjs } from '@/utilities';

import CalendarEventKeyframe from './CalendarEventKeyframe';
import {
    CALENDAR_EVENT_SOURCE_IDS,
    displayCalendarEventNumber,
    type CalendarSelection,
    type DividendCalendarEvent,
    type EconomicCalendarEvent,
    type EconomicEventImpact,
    type MarketCalendarEvent,
} from './calendarEvents';

interface CalendarSelectedDaySiderProps {
    selection?: CalendarSelection;
}

const impactColor: Record<Exclude<EconomicEventImpact, 'unknown'>, string> = {
    high: 'red',
    low: 'blue',
    medium: 'gold',
};

const EconomicEventMetadata: FC<{ event: EconomicCalendarEvent }> = ({ event }) => {
    const context = [event.country, event.currency].filter(Boolean).join(' · ');

    return (
        <Flex gap={8} vertical>
            <Flex align='center' gap={6} wrap>
                {event.impact !== 'unknown' ? (
                    <Tag bordered={false} color={impactColor[event.impact]} style={{ marginInlineEnd: 0 }}>
                        {event.impact} impact
                    </Tag>
                ) : null}
                {context ? <Typography.Text type='secondary'>{context}</Typography.Text> : null}
            </Flex>
            <Descriptions
                column={1}
                items={[
                    {
                        children: displayCalendarEventNumber(event.actual, event.unit),
                        key: 'actual',
                        label: 'Actual',
                    },
                    {
                        children: displayCalendarEventNumber(event.estimate, event.unit),
                        key: 'estimate',
                        label: 'Estimate',
                    },
                    {
                        children: displayCalendarEventNumber(event.previous, event.unit),
                        key: 'previous',
                        label: 'Previous',
                    },
                ]}
                size='small'
            />
        </Flex>
    );
};

const DividendEventMetadata: FC<{ event: DividendCalendarEvent }> = ({ event }) => (
    <Descriptions
        column={1}
        items={[
            {
                children: displayCalendarEventNumber(event.dividend),
                key: 'dividend',
                label: 'Dividend',
            },
            {
                children: displayCalendarEventNumber(event.adjustedDividend),
                key: 'adjusted-dividend',
                label: 'Adjusted dividend',
            },
            {
                children: event.declarationDate ? dayjs(event.declarationDate).format('ll') : '—',
                key: 'declaration-date',
                label: 'Declared',
            },
            {
                children: event.recordDate ? dayjs(event.recordDate).format('ll') : '—',
                key: 'record-date',
                label: 'Record date',
            },
            {
                children: event.paymentDate ? dayjs(event.paymentDate).format('ll') : '—',
                key: 'payment-date',
                label: 'Payment date',
            },
        ]}
        size='small'
    />
);

const EventDetailsCard: FC<{ event: MarketCalendarEvent }> = ({ event }) => {
    const sourceLabel = event.source === CALENDAR_EVENT_SOURCE_IDS.dividend ? 'Dividend' : 'Economic';
    const eventLabel = event.source === CALENDAR_EVENT_SOURCE_IDS.dividend ? event.symbol : event.title;

    return (
        <Card
            size='small'
            title={
                <Flex align='center' gap={6} style={{ minWidth: 0 }}>
                    <CalendarEventKeyframe source={event.source} />
                    <Typography.Text ellipsis strong>
                        {eventLabel}
                    </Typography.Text>
                </Flex>
            }
            extra={<SmallTag message={sourceLabel} />}
        >
            {event.startsAt.length > 10 ? (
                <Typography.Text style={{ display: 'block', marginBottom: '0.5rem' }} type='secondary'>
                    {dayjs(event.startsAt).format('LT')}
                </Typography.Text>
            ) : null}
            {event.source === CALENDAR_EVENT_SOURCE_IDS.economic ? (
                <EconomicEventMetadata event={event} />
            ) : (
                <DividendEventMetadata event={event} />
            )}
        </Card>
    );
};

const CalendarSelectedDaySider: FC<CalendarSelectedDaySiderProps> = ({ selection }) => {
    const { token } = useAntDesignTheme();

    return (
        <aside
            aria-label='Selected day details'
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}
        >
            <Flex
                align='center'
                gap={8}
                style={{ borderBottom: `1px solid ${token.colorBorder}`, flex: 'none', padding: '0.75rem' }}
            >
                <Avatar
                    icon={<TbCalendarEvent size='1rem' />}
                    shape='square'
                    size='small'
                    style={{
                        backgroundColor: token.colorFillQuaternary,
                        border: `1px solid ${token.colorBorder}`,
                        color: token.colorTextSecondary,
                    }}
                />
                <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                    <Typography.Text ellipsis strong>
                        {selection ? dayjs(selection.date).format('dddd, MMMM D') : 'Day Details'}
                    </Typography.Text>
                    <Typography.Text ellipsis style={{ color: token.colorTextTertiary, display: 'block' }}>
                        {selection ? dayjs(selection.date).format('YYYY') : 'Select a date from the calendar'}
                    </Typography.Text>
                </div>
                {selection ? <SmallTag compact message={selection.events.length.toLocaleString()} /> : null}
            </Flex>

            <OverflowFadeScrollArea
                aria-label='Selected day events'
                containerStyle={{ flex: '1 1 0%', minHeight: 0 }}
                surface='container'
            >
                {!selection ? (
                    <EmptySection
                        description='Choose a calendar day to review its economic releases and dividend events.'
                        height='100%'
                        icon={<TbCalendarSearch />}
                        label='Select a Day'
                    />
                ) : selection.events.length ? (
                    <Flex gap={8} style={{ padding: '0.75rem' }} vertical>
                        {selection.events.map((event) => (
                            <EventDetailsCard key={event.id} event={event} />
                        ))}
                    </Flex>
                ) : (
                    <EmptySection
                        description='There are no dividend or economic events scheduled for this date.'
                        height='100%'
                        icon={<TbCalendarEvent />}
                        label='No Events Scheduled'
                    />
                )}
            </OverflowFadeScrollArea>
        </aside>
    );
};

export default CalendarSelectedDaySider;
